import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Star, Clock, CheckCircle, Phone, ArrowRight, Loader2,
  Droplet, Zap, Thermometer, Wrench, Sparkles, Car, BadgeCheck, Search, Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
};

const serviceDescriptions = {
  plumbing: "Professional plumbing services including repairs, installations, and maintenance. From leaky faucets to full bathroom remodels.",
  electrical: "Licensed electricians for all your electrical needs. Wiring, panel upgrades, outlet installations, and safety inspections.",
  hvac: "Heating, ventilation, and air conditioning experts. AC repair, furnace maintenance, and system installations.",
  handyman: "General home repair and maintenance services. Assembly, minor repairs, painting, and odd jobs around the house.",
  cleaning: "Professional cleaning services for homes and offices. Deep cleaning, move-out cleaning, and regular maintenance.",
  mechanics: "Auto repair and maintenance services. Oil changes, brake repairs, engine diagnostics, and more."
};

const ServiceLocationPage = () => {
  const { location, service } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchPageData();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, service]);

  const fetchPageData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/services/${location}/${service}`);
      setPageData(response.data);
    } catch (error) {
      console.error("Error fetching page data:", error);
      toast.error("Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-custom py-20 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8]" />
        </div>
      </div>
    );
  }

  const Icon = serviceIcons[service];
  const serviceTitle = service.charAt(0).toUpperCase() + service.slice(1);
  const locationTitle = location.charAt(0).toUpperCase() + location.slice(1);

  return (
    <div className="min-h-screen bg-white" data-testid="service-location-page">
      <Navbar />

      {/* Hero Section - SEO Optimized */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#e8f0fe] to-white">
        <div className="container-custom text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#dadce0] mb-6">
            {Icon && <Icon className="w-5 h-5 text-[#1a73e8]" />}
            <span className="text-[#5f6368]">{serviceTitle} Services</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#202124] mb-6">
            {pageData?.seo?.title || `${serviceTitle} Services in ${locationTitle}`}
          </h1>
          
          <p className="text-lg text-[#5f6368] max-w-2xl mx-auto mb-8">
            {pageData?.seo?.description || serviceDescriptions[service]}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/request")}
              className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto text-base"
              data-testid="get-quotes-btn"
            >
              Get Free Quotes
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/jobs?service=${service}&location=${location}`)}
              className="rounded-full px-8 py-3 h-auto text-base border-[#dadce0]"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              View Jobs
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-12">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1a73e8]">{pageData?.total_workers || 0}+</div>
              <div className="text-sm text-[#5f6368]">Local Pros</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1a73e8]">4.8</div>
              <div className="text-sm text-[#5f6368]">Avg Rating</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1a73e8]">24h</div>
              <div className="text-sm text-[#5f6368]">Avg Response</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contractors Section */}
      <section className="py-16">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-medium text-[#202124] text-center mb-4">
            Top {serviceTitle} Contractors in {locationTitle}
          </h2>
          <p className="text-[#5f6368] text-center mb-12 max-w-xl mx-auto">
            Verified professionals ready to help with your {service} needs
          </p>

          {pageData?.workers?.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-[#202124] mb-2">No contractors found</h3>
              <p className="text-[#5f6368] mb-6">
                We're expanding to {locationTitle}. Be the first to join!
              </p>
              <Button
                onClick={() => navigate("/join")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
              >
                Join as a Pro
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pageData?.workers?.map((worker) => (
                <Link
                  key={worker.id}
                  to={`/profile/${worker.id}`}
                  className="bg-white rounded-2xl border border-[#dadce0] p-6 hover:shadow-lg transition-shadow"
                  data-testid={`worker-card-${worker.id}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-[#e8f0fe] flex items-center justify-center overflow-hidden shrink-0">
                      {worker.profile_photo ? (
                        <img src={worker.profile_photo} alt={worker.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl font-medium text-[#1a73e8]">
                          {worker.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[#202124] truncate">
                          {worker.business_name || worker.name}
                        </h3>
                        {worker.is_verified && (
                          <BadgeCheck className="w-4 h-4 text-[#1a73e8] shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="w-4 h-4 text-[#f9ab00] fill-[#f9ab00]" />
                        <span className="text-sm text-[#202124]">{worker.rating?.toFixed(1) || "New"}</span>
                        <span className="text-sm text-[#5f6368]">({worker.reviews_count} reviews)</span>
                      </div>
                    </div>
                    {worker.is_featured && (
                      <span className="px-2 py-1 bg-[#1a73e8] text-white text-xs rounded-full shrink-0">
                        Featured
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-[#5f6368] mb-4 line-clamp-2">{worker.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-[#dadce0]">
                    <div className="flex items-center gap-4 text-sm text-[#5f6368]">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {worker.location}
                      </span>
                    </div>
                    <span className="font-medium text-[#1a73e8]">${worker.hourly_rate}/hr</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {pageData?.workers?.length > 0 && (
            <div className="text-center mt-8">
              <Button
                variant="outline"
                onClick={() => navigate(`/browse?service=${service}&location=${location}`)}
                className="rounded-full border-[#dadce0]"
              >
                View All Contractors
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Available Jobs Section */}
      {pageData?.jobs?.length > 0 && (
        <section className="py-16 bg-[#f8f9fa]">
          <div className="container-custom">
            <h2 className="text-2xl md:text-3xl font-medium text-[#202124] text-center mb-4">
              Latest {serviceTitle} Jobs in {locationTitle}
            </h2>
            <p className="text-[#5f6368] text-center mb-12">
              Open jobs looking for contractors
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {pageData.jobs.slice(0, 4).map((job) => (
                <div
                  key={job.id}
                  className="bg-white rounded-xl border border-[#dadce0] p-6"
                  data-testid={`job-${job.id}`}
                >
                  <h3 className="font-medium text-[#202124] mb-2">{job.title}</h3>
                  <p className="text-sm text-[#5f6368] mb-4 line-clamp-2">{job.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#5f6368]">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {job.location}
                    </span>
                    <Link
                      to={`/jobs`}
                      className="text-[#1a73e8] text-sm hover:underline"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button
                onClick={() => navigate(`/jobs?service=${service}`)}
                className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
              >
                View All Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Other Locations */}
      <section className="py-16">
        <div className="container-custom">
          <h2 className="text-2xl font-medium text-[#202124] text-center mb-8">
            {serviceTitle} Services in Other Cities
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {locations
              .filter(loc => loc.toLowerCase() !== location.toLowerCase())
              .slice(0, 12)
              .map((loc) => (
                <Link
                  key={loc}
                  to={`/services/${loc.toLowerCase()}/${service}`}
                  className="px-4 py-2 bg-[#f8f9fa] text-[#5f6368] rounded-full hover:bg-[#e8f0fe] hover:text-[#1a73e8] transition-colors text-sm"
                >
                  {loc}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a73e8]">
        <div className="container-custom text-center text-white">
          <h2 className="text-2xl md:text-3xl font-medium mb-4">
            Need a {serviceTitle.toLowerCase()} in {locationTitle}?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Get matched with top-rated local professionals. Free quotes, no obligation.
          </p>
          <Button
            onClick={() => navigate("/request")}
            className="bg-white text-[#1a73e8] hover:bg-[#f8f9fa] rounded-full px-8 py-3 h-auto text-base"
          >
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16">
        <div className="container-custom max-w-3xl">
          <h2 className="text-2xl font-medium text-[#202124] mb-6">
            About {serviceTitle} Services in {locationTitle}
          </h2>
          <div className="prose prose-gray">
            <p className="text-[#5f6368] mb-4">
              {serviceDescriptions[service]}
            </p>
            <p className="text-[#5f6368] mb-4">
              LocalPros connects you with verified {service} professionals in {locationTitle} and surrounding areas. 
              Our contractors are background-checked, reviewed by real customers, and committed to quality service.
            </p>
            <h3 className="text-xl font-medium text-[#202124] mt-8 mb-4">
              Why Choose LocalPros for {serviceTitle}?
            </h3>
            <ul className="space-y-2 text-[#5f6368]">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Verified and background-checked professionals</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Real customer reviews and ratings</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Free quotes with no obligation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Fast response times from local contractors</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceLocationPage;
